/**
 * @engine/vfx - GPU Event Nodes
 *
 * Nodes for GPU-side particle events (e.g., spawn on death).
 * This enables multi-stage effects like fireworks where dying particles
 * trigger spawns in another system.
 *
 * Architecture:
 * - Trigger nodes (On Die, Over Time, Over Distance, On Collide) have an 'event' output
 * - GPU Event node receives the trigger and configures spawn count
 * - Compiler auto-generates eventId based on connections
 */

import type { VFXNodeDefinition } from '../types';
import { dataPort, flowPort } from '../types';

/**
 * GPU Event node definitions.
 */
export const EVENT_NODES: readonly VFXNodeDefinition[] = [
  // ---- GPU Event Output (receives triggers, writes to event buffer) ----
  {
    id: 'vfx.output.gpu_event',
    name: 'GPU Event',
    category: 'output',
    contexts: ['update'],
    description: 'Writes particle data to GPU event buffer. Connect trigger nodes to this. EventId is auto-assigned by compiler.',
    color: '#E91E63',
    inputs: [
      dataPort('trigger', 'Trigger', 'event'),
    ],
    outputs: [],
    modifiesParticle: false,
    // Actual shader code is generated by the update stage based on the connected trigger
  },

  // ---- Trigger On Die ----
  {
    id: 'vfx.trigger.on_die',
    name: 'On Die',
    category: 'utility',
    contexts: ['update'],
    description: 'Triggers when particle dies (lifetime >= maxLifetime). Connect to GPU Event node.',
    color: '#FF5722',
    inputs: [
      dataPort('count', 'Count', 'int', { defaultValue: 1, range: { min: 1, max: 100 }, description: 'Particles to spawn per death' }),
    ],
    outputs: [
      dataPort('event', 'Event', 'event'),
    ],
    modifiesParticle: false,
    // Trigger condition is emitted by the update stage's death block
  },

  // ---- Transit (default block for GPU Event context) ----
  {
    id: 'vfx.event.transit',
    name: 'Transit',
    category: 'spawn',
    contexts: ['event'],
    description: 'Receives external event and triggers spawn',
    color: '#E91E63',
    inputs: [
      dataPort('event_in', 'Event', 'event'),
    ],
    outputs: [],
    modifiesParticle: false,
    locked: true,
    // Transit is structural — it links a trigger to an event-driven system
  },

  // ---- Spawn On Event (GPU Events Only) ----
  {
    id: 'vfx.spawn.on_event',
    name: 'Spawn On Event',
    category: 'spawn',
    contexts: ['spawn'],
    description: 'Spawn particles in response to GPU events from another system (e.g., particle death).',
    color: '#E91E63',
    inputs: [
      dataPort('eventId', 'Event ID', 'uint', { defaultValue: 0 }),
      dataPort('ratio', 'Spawn Ratio', 'float', { defaultValue: 1.0, range: { min: 0, max: 10 } }),
    ],
    outputs: [
      flowPort('spawn', 'Spawn'),
    ],
    modifiesParticle: false,
    // This is handled specially in codegen - reads from event buffer
  },

  // ---- Inherit Source Position ----
  {
    id: 'vfx.init.inherit_source_position',
    name: 'Inherit Source Position',
    category: 'init',
    contexts: ['initialize'],
    description: 'Set position from source event (for event-spawned particles)',
    color: '#E91E63',
    inputs: [],
    outputs: [],
    modifiesParticle: true,
    // Applied by the init stage epilogue (after all other init nodes)
  },

  // ---- Inherit Source Velocity ----
  {
    id: 'vfx.init.inherit_source_velocity',
    name: 'Inherit Source Velocity',
    category: 'init',
    contexts: ['initialize'],
    description: 'Set velocity from source event (for event-spawned particles)',
    color: '#E91E63',
    inputs: [
      dataPort('scale', 'Scale', 'float', { defaultValue: 1.0, range: { min: 0, max: 10 } }),
    ],
    outputs: [],
    modifiesParticle: true,
    // Applied by the init stage epilogue (velocity is ADDED, scaled by `scale`)
  },

  // ---- Inherit Source Color ----
  {
    id: 'vfx.init.inherit_source_color',
    name: 'Inherit Source Color',
    category: 'init',
    contexts: ['initialize'],
    description: 'Set color from source event (for event-spawned particles)',
    color: '#E91E63',
    inputs: [],
    outputs: [],
    modifiesParticle: true,
    // Applied by the init stage epilogue
  },
];
